import { useCallback } from "react";
import { MultiUploadedFileResponse, UploadElementType } from "@custom-types/file-upload-types";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import { EventProps } from "@faro-lotv/foreign-observers";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { COOKIES_ERROR_MESSAGE, CoreFileUploader } from "@utils/core-file-uploader";

type ReturnFunction = (
  uploadedResponse: MultiUploadedFileResponse,
  elementType: UploadElementType,
) => Promise<void>;

/** Tracks an Amplitude event for a finished file upload. */
export function useUploadFilesTrackFinish(): ReturnFunction {
  const { trackEvent } = useTrackEvent();
  const { uploadManager } = useFileUploadContext();

  return useCallback(
    async(
      uploadedResponse,
      elementType
    ): Promise<void> => {
      const successfulUploads = uploadedResponse.successful;
      const failedUploads = uploadedResponse.failed;
      const canceledUploads = uploadedResponse.canceled;
      const uploadSizeMB = uploadedResponse.uploadSizeMB;
      const uploadTimeSecs = uploadedResponse.uploadTimeSecs;

      const propsToSend: EventProps = {
        type: elementType,
        successfulUploads: successfulUploads.length,
        failedUploads: failedUploads.length,
        canceledUploads: canceledUploads.length,
        failedOrCanceledUploads: failedUploads.length + canceledUploads.length,
        uploadSizeMB,
        uploadTimeSecs,
        // When finishing the upload, the Promise is already resolved.
        uploadEndpoint: await CoreFileUploader.getUploadEndpoint().catch(() => ""),
        isSharedWorker: uploadManager.isSharedWorker(),
      };

      // For tiny uploads, the speed will be inaccurate, and therefore we better don't report it.
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      if (uploadSizeMB >= 20 && uploadedResponse.uploadSpeedMBps && uploadedResponse.uploadSpeedBucketMBps) {
        propsToSend.uploadSpeedMBps = uploadedResponse.uploadSpeedMBps;
        propsToSend.uploadSpeedBucketMBps = uploadedResponse.uploadSpeedBucketMBps;
      }

      const areCookiesToBlame = failedUploads.some((failedUpload) =>
        failedUpload.errorMessage === COOKIES_ERROR_MESSAGE
      );
      if (failedUploads.length > 0) {
        propsToSend.areCookiesToBlame = areCookiesToBlame;
        propsToSend.errorMessage = failedUploads[0].errorMessage;
      }

      trackEvent({
        name: DataManagementEvents.finishUpload,
        props: propsToSend,
      });
    },
    [trackEvent, uploadManager]
  );
}
